import essApi from "../axios/essApi";

class SupportService {
  // ─── Tickets ──────────────────────────────────────────────────────────────

  async getMyTickets(param) {
    const {
      pageNum = 0,
      pageSize = 10,
      status = "",
      priority = "",
      category = "",
      search = "",
    } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/tickets/my?page=${pageNum}&size=${pageSize}&status=${status}&priority=${priority}&category=${category}&search=${search}`,
      ),
    );
  }

  async getSeniorInbox() {
    return Promise.resolve(essApi.get(`support/tickets/senior-inbox`));
  }

  async getAllTickets(param) {
    const {
      pageNum = 0,
      pageSize = 10,
      status = "",
      priority = "",
      agentId = "",
      assignedTo = "",
      search = "",
      fromDate = "",
      toDate = "",
      resolvedFrom = "",
      departmentId = "",
      excludeStatuses = "",
    } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/tickets?page=${pageNum}&size=${pageSize}&status=${status}&priority=${priority}&agentId=${agentId}&assignedTo=${assignedTo}&search=${search}&fromDate=${fromDate}&toDate=${toDate}&resolvedFrom=${resolvedFrom}&departmentId=${departmentId}&excludeStatuses=${excludeStatuses}`,
      ),
    );
  }

  async getTicketById(id) {
    return Promise.resolve(essApi.get(`support/tickets/${id}`));
  }

  // body: { subject, description, categoryId, subcategory, priority, relatedModule }
  async createTicket(param) {
    return Promise.resolve(essApi.post(`support/tickets`, param));
  }

  // body: { status, priority, assignedTo }
  async updateTicket(id, param) {
    return Promise.resolve(essApi.put(`support/tickets/${id}`, param));
  }

  async resolveTicket(id) {
    return Promise.resolve(
      essApi.put(`support/tickets/${id}`, { status: "Resolved" }),
    );
  }

  async escalateTicket(id) {
    return Promise.resolve(
      essApi.put(`support/tickets/${id}`, { priority: "Critical" }),
    );
  }

  async closeTicket(id) {
    return Promise.resolve(
      essApi.put(`support/tickets/${id}`, { status: "Closed" }),
    );
  }

  async reopenTicket(id) {
    return Promise.resolve(essApi.post(`support/tickets/${id}/reopen`, {}));
  }

  // body: { rating: 1-5 }
  async submitCsat(id, param) {
    return Promise.resolve(essApi.post(`support/tickets/${id}/csat`, param));
  }

  // ─── Comments ─────────────────────────────────────────────────────────────

  async getComments(ticketId) {
    return Promise.resolve(essApi.get(`support/tickets/${ticketId}/comments`));
  }

  // body: { message, isInternalNote: bool }
  async addComment(ticketId, param) {
    return Promise.resolve(
      essApi.post(`support/tickets/${ticketId}/comments`, param),
    );
  }

  // ─── Attachments ──────────────────────────────────────────────────────────

  async getAttachments(ticketId) {
    return Promise.resolve(
      essApi.get(`support/tickets/${ticketId}/attachments`),
    );
  }

  async uploadAttachment(ticketId, files) {
    const formData = new FormData();
    // append files correctly
    files.forEach((file) => {
      formData.append("files", file);
    });
    return essApi.post(`support/tickets/${ticketId}/attachments`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
  }

  async downloadAttachment(ticketId, attachmentId) {
    return essApi.get(
      `support/tickets/${ticketId}/attachments/${attachmentId}/download`,
      {
        responseType: "blob",
      },
    );
  }

  async deleteAttachment(ticketId, attachmentId) {
    return Promise.resolve(
      essApi.delete(`support/tickets/${ticketId}/attachments/${attachmentId}`),
    );
  }

  // body: { userIds: number[], comment }
  async requestApproval(ticketId, param) {
    return Promise.resolve(essApi.post(`support/tickets/${ticketId}/request-approval`, param));
  }

  // body: { decision: 'approved'|'rejected', comment }
  async respondApproval(ticketId, commentId, param) {
    return Promise.resolve(essApi.post(`support/tickets/${ticketId}/comments/${commentId}/respond-approval`, param));
  }

  // ─── Activity Log ─────────────────────────────────────────────────────────

  async getActivityLog(ticketId) {
    return Promise.resolve(essApi.get(`support/tickets/${ticketId}/activity`));
  }

  // ─── Categories ───────────────────────────────────────────────────────────

  async getCategories(param) {
    const { departmentId = "" } = param ?? {};
    return Promise.resolve(essApi.get(`support/categories?departmentId=${departmentId}`));
  }

  async getCategoryById(id) {
    return Promise.resolve(essApi.get(`support/categories/${id}`));
  }

  // body: { name, icon, description }
  async createCategory(param) {
    return Promise.resolve(essApi.post(`support/categories`, param));
  }

  // body: { name, icon, description, isActive }
  async updateCategory(id, param) {
    return Promise.resolve(essApi.put(`support/categories/${id}`, param));
  }

  async deleteCategory(id) {
    return Promise.resolve(essApi.delete(`support/categories/${id}`));
  }

  // ─── Sub-Categories ───────────────────────────────────────────────────────

  async getSubCategories(categoryId) {
    return Promise.resolve(essApi.get(`support/categories/${categoryId}/subcategories`));
  }

  async createSubCategory(param) {
    return Promise.resolve(essApi.post(`support/subcategories`, param));
  }

  async updateSubCategory(id, param) {
    return Promise.resolve(essApi.put(`support/subcategories/${id}`, param));
  }

  async deleteSubCategory(id) {
    return Promise.resolve(essApi.delete(`support/subcategories/${id}`));
  }

  // ─── Users ────────────────────────────────────────────────────────────────

  async getUsers() {
    return Promise.resolve(essApi.get(`users/getallactiveusers`));
  }

  async getActiveUsers() {
    return Promise.resolve(essApi.get(`users/getallactiveusers`));
  }

  // ─── Agents ───────────────────────────────────────────────────────────────

  async getAgentsByDepartment(departmentId) {
    return Promise.resolve(essApi.get(`support/agents/by-department/${departmentId}`));
  }

  async getAgents(param) {
    const { departmentId = "" } = param ?? {};
    return Promise.resolve(essApi.get(`support/agents?departmentId=${departmentId}`));
  }

  async getAgentById(id) {
    return Promise.resolve(essApi.get(`support/agents/${id}`));
  }

  // body: { userId, role, department, categories: [], isActive }
  async createAgent(param) {
    return Promise.resolve(essApi.post(`support/agents`, param));
  }

  // body: { role, department, categories: [], isActive }
  async updateAgent(id, param) {
    return Promise.resolve(essApi.put(`support/agents/${id}`, param));
  }

  async getAgentQueue(agentId, param) {
    const { pageNum = 0, pageSize = 20 } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/agents/${agentId}/queue?page=${pageNum}&size=${pageSize}`,
      ),
    );
  }

  // ─── SLA Config ───────────────────────────────────────────────────────────

  async getSlaConfig(param) {
    const { departmentId = "" } = param ?? {};
    return Promise.resolve(essApi.get(`support/sla-config?departmentId=${departmentId}`));
  }

  // Get SLA config scoped to a specific category's department (used by NewTicket)
  async getSlaConfigByCategory(categoryId) {
    return Promise.resolve(essApi.get(`support/sla-config/by-category/${categoryId}`));
  }

  // body: [{ priority, firstResponseHrs, resolutionHrs, escalateAfterHrs }]
  async updateSlaConfig(param) {
    return Promise.resolve(essApi.put(`support/sla-config`, param));
  }

  async getSlaSettings(param) {
    const { departmentId = "" } = param ?? {};
    return Promise.resolve(essApi.get(`support/sla-settings?departmentId=${departmentId}`));
  }

  // body: { emailAlerts, autoEscalate, csatSurvey, weekendPause }
  async updateSlaSettings(param) {
    return Promise.resolve(essApi.put(`support/sla-settings`, param));
  }

  // Live SLA status for a single ticket
  async getTicketSlaStatus(ticketId) {
    return Promise.resolve(essApi.get(`support/tickets/${ticketId}/sla`));
  }

  // Admin: recompute SLA on all open tickets
  async refreshAllSla() {
    return Promise.resolve(essApi.post(`support/sla/refresh`, {}));
  }

  // Preview who would be auto-assigned for a category (used in NewTicket form)
  async previewAssignment(categoryId) {
    return Promise.resolve(
      essApi.get(`support/tickets/preview-assignment?categoryId=${categoryId}`),
    );
  }

  // ─── Auto-Assignment Rules ────────────────────────────────────────────────

  async getAutoAssignmentRules(param) {
    const { departmentId = "" } = param ?? {};
    return Promise.resolve(essApi.get(`support/auto-assignments?departmentId=${departmentId}`));
  }

  // body: [{ categoryId, agentId, ruleType: 'direct'|'round_robin' }]
  async updateAutoAssignmentRules(param) {
    return Promise.resolve(essApi.put(`support/auto-assignments`, param));
  }

  // ─── Knowledge Base ───────────────────────────────────────────────────────

  async getKbArticles(param) {
    const {
      categoryId = "",
      search = "",
      pageNum = 0,
      pageSize = 20,
    } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/kb-articles?categoryId=${categoryId}&search=${search}&page=${pageNum}&size=${pageSize}`,
      ),
    );
  }

  async getKbArticleById(id) {
    return Promise.resolve(essApi.get(`support/kb-articles/${id}`));
  }

  // body: { categoryId, title, content, isPublished }
  async createKbArticle(param) {
    return Promise.resolve(essApi.post(`support/kb-articles`, param));
  }

  // body: { categoryId, title, content, isPublished }
  async updateKbArticle(id, param) {
    return Promise.resolve(essApi.put(`support/kb-articles/${id}`, param));
  }

  async deleteKbArticle(id) {
    return Promise.resolve(essApi.delete(`support/kb-articles/${id}`));
  }

  // ─── Reports & Dashboard ──────────────────────────────────────────────────

  async getAdminDashboardStats(param) {
    const { fromDate = "", toDate = "", departmentId = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/reports/dashboard?fromDate=${fromDate}&toDate=${toDate}&departmentId=${departmentId}`,
      ),
    );
  }

  async getTicketsByCategory(param) {
    const { fromDate = "", toDate = "", departmentId = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/reports/by-category?fromDate=${fromDate}&toDate=${toDate}&departmentId=${departmentId}`,
      ),
    );
  }

  async getAgentPerformance(param) {
    const { fromDate = "", toDate = "", departmentId = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/reports/agent-performance?fromDate=${fromDate}&toDate=${toDate}&departmentId=${departmentId}`,
      ),
    );
  }

  async getMonthlyTrend(param) {
    const { year = new Date().getFullYear(), departmentId = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(`support/reports/monthly-trend?year=${year}&departmentId=${departmentId}`),
    );
  }

  async getSlaComplianceReport(param) {
    const { fromDate = "", toDate = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(
        `support/reports/sla-compliance?fromDate=${fromDate}&toDate=${toDate}`,
      ),
    );
  }

  async getCsatReport(param) {
    const { fromDate = "", toDate = "" } = param ?? {};
    return Promise.resolve(
      essApi.get(`support/reports/csat?fromDate=${fromDate}&toDate=${toDate}`),
    );
  }

  // body: { reportType, frequency, recipients: [] }
  async scheduleReport(param) {
    return Promise.resolve(essApi.post(`support/reports/schedule`, param));
  }

  // body: { reportType, fromDate, toDate, format: 'excel'|'pdf' }
  async downloadReport(param) {
    const { reportType, fromDate, toDate, format, departmentId = "" } = param;
    return Promise.resolve(
      essApi.get(
        `support/reports/download?reportType=${reportType}&fromDate=${fromDate}&toDate=${toDate}&format=${format}&departmentId=${departmentId}`,
        { responseType: "blob" },
      ),
    );
  }

  // ── Working Hours ──────────────────────────────────────────────────────────────────────────────────

  async getWorkingHours(departmentId) {
    return Promise.resolve(essApi.get(`support/working-hours?departmentId=${departmentId}`));
  }

  // body: { departmentId, days: [{ dayOfWeek, isWorking, startTime, endTime }] }
  async updateWorkingHours(param) {
    return Promise.resolve(essApi.put(`support/working-hours`, param));
  }

  // ── Departments ────────────────────────────────────────────────────────────────────────────────────

  async getDepartments() {
    return Promise.resolve(essApi.get(`support/departments`));
  }

  // ── Domain Lookups ─────────────────────────────────────────────────────────────────────────────────

  async getDomainLookupByKey(key) {
    return Promise.resolve(essApi.get(`domain-lookups?key=${key}`));
  }
}

export default SupportService;
